import {
  ArrowRightCircleIcon,
  DownloadIcon,
  FileTextIcon,
  FolderPlusIcon,
  LockIcon,
  TrashIcon,
} from "lucide-react";
import { CloseIcon } from "../../CloseIcon";
import { Button } from "../../tailwind/Button";
import { MoveItemsModal } from "@/src/routes/data_room/MoveItemsModal";
import { useContext, useEffect, useRef, useState } from "react";
import { DeleteItemsModal } from "@/src/routes/data_room/DeleteItemsModal";
import {
  DataRoomContext,
  DataRoomFileDocument,
  DataRoomFileQuery,
  DataRoomFileQueryVariables,
  DataRoomFolder,
  DataRoomFolderBrowseFragmentFragment,
  DataRoomFolderBrowseQuery,
  DataRoomPermission,
  DealActivityType,
  DealExportStatus,
  DealExportTraverseMode,
  FileDownloadUrlDocument,
  FileDownloadUrlQuery,
  FileDownloadUrlQueryVariables,
  FileType,
  UpdateDataRoomFileDocument,
  UpdateDataRoomFileMutation,
  UpdateDataRoomFileMutationVariables,
  UpdateDataRoomFolderDocument,
  UpdateDataRoomFolderMutation,
  UpdateDataRoomFolderMutationVariables,
  useCreateDataRoomFolderMutation,
  useCreateDealExportMutation,
  useDataRoomFolderBrowseQuery,
  useDealExportsQuery,
  useTrackActivityMutation,
} from "@/src/graphql/generated";
import { DataRoomContext as DataRoomUiContext } from "@/src/routes/data_room";
import { DownloadsModal } from "@/src/routes/data_room/DownloadsModal";
import { useDataRoomIndexPdf } from "@/src/routes/data_room/export/useDataRoomIndexPdf";
import useGqlClient from "@/src/hooks/useGqlClient";
import saveAs from "file-saver";
import { toasts } from "../../toasts/toasts";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { dataRoomSelectors } from "@/src/store/data-room/selector";
import { authSelectors } from "@/src/store/auth/selector";
import { useSelector } from "react-redux";
import { AppState } from "@/src/store";
import { fromUnixTime, isAfter, subMinutes } from "date-fns";
import { WriteAccessOnly } from "../DataRoomHeader";
import { DocumentPlusIcon } from "@heroicons/react/24/solid";
import { useUploadContext } from "../file_uploaders/useUploadContext";
import { AnimatedModal } from "../../AnimatedModal";
import { NewFilesUploader } from "../file_uploaders/NewFilesUploader";
import { TextInput } from "../../tailwind/TextInput";
import { H3 } from "../../Heading";
import { useInvalidateQueryKeys } from "@/src/hooks/useInvalidateQueryKeys";
import { PermissionCheck } from "../../permissions/PermissionCheck";
import Dropdown, { Option } from "../../tailwind/Dropdown";

interface Props {
  folderId: string;
  permissions: Pick<DataRoomPermission, "id" | "name" | "description">[];
  selectedFolders: string[];
  selectedFiles: string[];
  setSelectedFolders: (folders: string[]) => void;
  setSelectedFiles: (files: string[]) => void;
}

export function SelectFilesActions(props: Props) {
  const [openModal, setOpenModal] = useState<
    | "move"
    | "download"
    | "delete"
    | "downloads"
    | "file"
    | "folder"
    | "permissions"
    | ""
  >("");
  const [isUpdatingPermissions, setIsUpdatingPermissions] = useState(false);

  const client = useGqlClient();
  const trackActivity = useTrackActivityMutation(client);
  const { dealId: activeDealId } = useParams<{ dealId: string }>();
  const { invalidateDataRoomFolders } = useInvalidateQueryKeys();

  const {
    uploadFiles,
    uploadFolders,
    uploadModalOpen,
    setUploadModalOpen,
    targetFolderId,
    setTargetFolderId,
    setTargetFolderName,
    clearCompleted,
  } = useUploadContext();
  const uploadQueueCount = uploadFiles.length + uploadFolders.length;

  useEffect(() => {
    if (uploadModalOpen) {
      setOpenModal("file");
    }
  }, [uploadModalOpen]);

  const dealExportsQuery = useDealExportsQuery(
    client,
    {
      dealId: activeDealId ?? "",
    },
    {
      enabled: !!activeDealId && openModal === "downloads",
    },
  );

  const uploadFolderId = targetFolderId || props.folderId;

  const { data, isLoading, error } = useDataRoomFolderBrowseQuery(
    client,
    {
      id: props.folderId,
    },
    {
      enabled: props.folderId !== "",
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  );

  const uploadFolderQuery = useDataRoomFolderBrowseQuery(
    client,
    {
      id: uploadFolderId,
    },
    {
      enabled: uploadFolderId !== "" && openModal === "file",
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  );

  function openUploadModal() {
    clearCompleted();
    setTargetFolderId(props.folderId);
    setTargetFolderName(data?.dataRoomFolder.name || "Home");
    setUploadModalOpen(true);
    setOpenModal("file");
  }

  function closeUploadModal() {
    setOpenModal("");
    setUploadModalOpen(false);
  }

  const createDealExport = useCreateDealExportMutation(client);
  const { dealId } = useParams<{ dealId: string }>();
  const queryClient = useQueryClient();

  const deal = useSelector((state: AppState) =>
    authSelectors.deal(state, dealId),
  );

  const dataRoomView = useSelector((state: AppState) =>
    dataRoomSelectors.dataRoomView(state, deal?.dataRoom.id ?? ""),
  );

  const { filters, indexingEnabled } = useContext(DataRoomUiContext);
  const { download: downloadIndexPdf, isLoading: isDownloadingIndexPdf } =
    useDataRoomIndexPdf(activeDealId ?? "");

  async function downloadFile(id: string) {
    try {
      const dataRoomFileQuery = await client.request<
        DataRoomFileQuery,
        DataRoomFileQueryVariables
      >(DataRoomFileDocument, {
        id,
      });

      if (!dataRoomFileQuery.dataRoomFile.hasDownloadAccess) {
        return;
      }

      client
        .request<FileDownloadUrlQuery, FileDownloadUrlQueryVariables>(
          FileDownloadUrlDocument,
          {
            id,
          },
        )
        .then((res) => {
          const downloadUrl = res.fileDownloadUrl.downloadUrl;
          fetch(downloadUrl).then(async (res) => {
            const blob = await res.blob();
            saveAs(blob, getSaveFileName(dataRoomFileQuery.dataRoomFile));
          });
          trackActivity.mutate({
            type: DealActivityType.FileDownloaded,
            fileId: id,
            dataRoomId: "",
            folderId: dataRoomFileQuery.dataRoomFile.folderId,
          });
        })
        .catch((err) => {
          toasts.error("Failed to download file");
        });
    } catch (e) {
      console.log(e);
    }
  }

  function createExport() {
    createDealExport.mutate(
      {
        input: {
          dealID: dealId,
          dataRoomFileIDs: props.selectedFiles,
          name: "",
          traverseMode:
            dataRoomView === "folder"
              ? DealExportTraverseMode.All
              : DealExportTraverseMode.Specific,
          dataRoomFolderIDs: props.selectedFolders,
        },
      },
      {
        onSuccess: () => {
          props.setSelectedFiles([]);
          props.setSelectedFolders([]);
          queryClient.invalidateQueries({
            queryKey: [
              "DealExports",
              {
                dealId: dealId,
              },
            ],
          });
          toasts.info("Generating download...");
        },
      },
    );
  }

  const nothingSelected =
    props.selectedFolders.length === 0 && props.selectedFiles.length === 0;
  const totalSelected =
    props.selectedFolders.length + props.selectedFiles.length;

  const exportingExports = dealExportsQuery.data?.deal.dealExports.filter(
    (de) =>
      de.status === DealExportStatus.Exporting &&
      isAfter(fromUnixTime(de.createdAt), subMinutes(new Date(), 60)),
  );

  return (
    <>
      {nothingSelected ? (
        <div>
          <div className="space-x-2 flex items-center">
            <Button
                variant="neutral"
                text="Exports"
                icon={DownloadIcon}
                size="s"
                trail={
                  exportingExports && exportingExports.length > 0
                    ? {
                        type: "spinner",
                      }
                    : {
                        type: "badge",
                        badge:
                          dealExportsQuery.data?.deal.dealExports.length ?? 0,
                      }
                }
                onClick={() => {
                  setOpenModal("downloads");
                }}
                menu={{
                  label: "More export options",
                  items: [
                    {
                      label: isDownloadingIndexPdf
                        ? "Generating..."
                        : "Download Data Room Index",
                      icon: FileTextIcon,
                      isLoading: isDownloadingIndexPdf,
                      onClick: () => {
                        downloadIndexPdf(filters, indexingEnabled);
                      },
                    },
                  ],
                }}
              />


            <DownloadsModal
              open={openModal === "downloads"}
              onClose={() => {
                setOpenModal("");
              }}
            />

            <WriteAccessOnly folderId={props.folderId}>
              <AnimatedModal
                size="md"
                open={openModal === "folder"}
                onClose={() => {
                  setOpenModal("");
                }}
              >
                <NewFolder
                  data={data}
                  permissions={props.permissions}
                  isLoading={isLoading}
                  error={error}
                  onClose={() => setOpenModal("")}
                />
              </AnimatedModal>
            </WriteAccessOnly>

            <WriteAccessOnly folderId={props.folderId}>
              <div className="hidden xl:block">
                <Button
                  icon={FolderPlusIcon}
                  text="New folder"
                  variant="neutral"
                  size="s"
                  onClick={() => {
                    setOpenModal("folder");
                  }}
                />
              </div>
              <div className="block xl:hidden">
                <Button
                  icon={FolderPlusIcon}
                  text=""
                  variant="neutral"
                  size="s"
                  onClick={() => {
                    setOpenModal("folder");
                  }}
                />
              </div>

              <div className="hidden xl:block">
                <div className="relative">
                  <Button
                    icon={DocumentPlusIcon}
                    text="Upload documents"
                    size="s"
                    variant="positive"
                    onClick={openUploadModal}
                  />
                  {uploadQueueCount > 0 ? (
                    <span className="absolute top-[-5px] right-[-5px] inline-flex items-center justify-center w-4 h-4 text-xs font-semibold text-white bg-red-500 rounded-full">
                      {uploadQueueCount}
                    </span>
                  ) : null}
                </div>
              </div>
              <div className="block xl:hidden">
                <Button
                  icon={DocumentPlusIcon}
                  text=""
                  size="s"
                  variant="positive"
                  onClick={openUploadModal}
                />
              </div>
            </WriteAccessOnly>
          </div>
        </div>
      ) : (
        <div className="flex items-center bg-concrete-100 box-content rounded-md px-2 py-1">
          <div className="flex items-center space-x-2">
            <CloseIcon
              onClose={() => {
                if (props.setSelectedFolders) props.setSelectedFolders([]);
                if (props.setSelectedFiles) props.setSelectedFiles([]);
              }}
            />
            <span className="hidden md:flex text-sm font-semibold text-gray-600">
              {formatSelectedCount(
                props.selectedFiles.length,
                props.selectedFolders.length,
              )}
            </span>
            <span className="md:hidden text-sm">{totalSelected} selected</span>
          </div>
          <div className="flex items-center space-x-2 ml-4">
            <PermissionCheck input={{ permissionKey: "documents:download" }}>
              <Button
                variant="neutral"
                text="Download"
                icon={DownloadIcon}
                size="s"
                onClick={() => {
                  if (
                    props.selectedFiles.length === 1 &&
                    props.selectedFolders.length === 0
                  ) {
                    downloadFile(props.selectedFiles[0]);
                    return;
                  }

                  createExport();
                }}
              />
            </PermissionCheck>
            <PermissionCheck input={{ permissionKey: "documents:edit" }}>
              <Button
                variant="neutral"
                text="Move"
                icon={ArrowRightCircleIcon}
                size="s"
                onClick={() => {
                  setOpenModal("move");
                }}
              />
            </PermissionCheck>

            <PermissionCheck input={{ permissionKey: "documents:edit" }}>
              <Button
                variant="neutral"
                text="Change permission"
                icon={LockIcon}
                size="s"
                onClick={() => {
                  setOpenModal("permissions");
                }}
              />
            </PermissionCheck>

            <PermissionCheck input={{ permissionKey: "documents:delete" }}>
              <Button
                variant="danger"
                text="Delete"
                icon={TrashIcon}
                size="s"
                onClick={() => {
                  setOpenModal("delete");
                }}
              />
            </PermissionCheck>
          </div>
          <MoveItemsModal
            open={openModal === "move"}
            onClose={() => {
              setOpenModal("");
              if (props.setSelectedFiles) props.setSelectedFiles([]);
              if (props.setSelectedFolders) props.setSelectedFolders([]);
            }}
            dataRoomFileIds={props.selectedFiles}
            dataRoomFolderIds={props.selectedFolders}
          />
          <DeleteItemsModal
            open={openModal === "delete"}
            onClose={() => {
              setOpenModal("");
            }}
            dataRoomFileIds={props.selectedFiles}
            dataRoomFolderIds={props.selectedFolders}
            onDelete={() => {
              setOpenModal("");
              if (props.setSelectedFiles) props.setSelectedFiles([]);
              if (props.setSelectedFolders) props.setSelectedFolders([]);
            }}
          />
          <ChangePermissionsModal
            open={openModal === "permissions"}
            onClose={() => setOpenModal("")}
            permissions={props.permissions}
            selectedFiles={props.selectedFiles}
            selectedFolders={props.selectedFolders}
            isLoading={isUpdatingPermissions}
            onConfirm={async (permissionId) => {
              setIsUpdatingPermissions(true);
              try {
                const results = await Promise.allSettled([
                  ...props.selectedFiles.map((id) =>
                    client.request<
                      UpdateDataRoomFileMutation,
                      UpdateDataRoomFileMutationVariables
                    >(UpdateDataRoomFileDocument, {
                      input: {
                        id,
                        permissionIDs: [permissionId],
                      },
                    }),
                  ),
                  ...props.selectedFolders.map((id) =>
                    client.request<
                      UpdateDataRoomFolderMutation,
                      UpdateDataRoomFolderMutationVariables
                    >(UpdateDataRoomFolderDocument, {
                      input: {
                        id,
                        permissionIDs: [permissionId],
                      },
                    }),
                  ),
                ]);

                const failed = results.filter(
                  (r) => r.status === "rejected",
                ).length;
                if (failed > 0) {
                  toasts.error(
                    `Failed to update permission on ${failed} item${failed === 1 ? "" : "s"}`,
                  );
                } else {
                  toasts.success("Permissions updated");
                  props.setSelectedFiles([]);
                  props.setSelectedFolders([]);
                  setOpenModal("");
                }
                invalidateDataRoomFolders();
              } catch {
                toasts.error("Failed to update permissions");
              } finally {
                setIsUpdatingPermissions(false);
              }
            }}
          />
        </div>
      )}

      <NewFilesUploader
        open={openModal === "file"}
        onClose={closeUploadModal}
        permissions={props.permissions}
        folder={
          (uploadFolderQuery.data?.dataRoomFolder ??
            data?.dataRoomFolder) as DataRoomFolder
        }
        isLoading={
          uploadFolderQuery.isLoading || (isLoading && !uploadFolderQuery.data)
        }
        error={uploadFolderQuery.error || error}
      />
    </>
  );
}

function ChangePermissionsModal(props: {
  open: boolean;
  onClose: () => void;
  permissions: Pick<DataRoomPermission, "id" | "name" | "description">[];
  selectedFiles: string[];
  selectedFolders: string[];
  isLoading: boolean;
  onConfirm: (permissionId: string) => void;
}) {
  const defaultPermission = props.permissions[0];
  const [permission, setPermission] = useState<Option>({
    label: defaultPermission?.name ?? "",
    value: defaultPermission?.id ?? "",
    description: defaultPermission?.description ?? "",
  });

  useEffect(() => {
    if (!props.open || !props.permissions[0]) return;
    setPermission({
      label: props.permissions[0].name,
      value: props.permissions[0].id,
      description: props.permissions[0].description,
    });
  }, [props.open, props.permissions]);

  const total = props.selectedFiles.length + props.selectedFolders.length;

  return (
    <AnimatedModal open={props.open} onClose={props.onClose} size="md">
      <div className="flex items-center justify-between">
        <H3>Change permission</H3>
        <CloseIcon onClose={props.onClose} />
      </div>
      <p className="text-sm text-gray-500 mt-1 mb-4">
        Apply a permission level to {total} selected item
        {total === 1 ? "" : "s"}.
      </p>
      <Dropdown
        label="Permission level"
        selectedOption={permission}
        onSelect={(option) => setPermission(option)}
        options={props.permissions.map((p) => ({
          label: p.name,
          value: p.id,
          description: p.description,
        }))}
      />
      <div className="flex justify-end gap-x-2 mt-6">
        <Button variant="neutral" text="Cancel" onClick={props.onClose} />
        <Button
          variant="positive"
          text="Update"
          isLoading={props.isLoading}
          isDisabled={!permission.value}
          onClick={() => {
            if (!permission.value) return;
            props.onConfirm(permission.value);
          }}
        />
      </div>
    </AnimatedModal>
  );
}

function NewFolder(props: {
  data: DataRoomFolderBrowseQuery | undefined;
  isLoading: boolean;
  error?: unknown;
  onClose: () => void;
  permissions: Pick<DataRoomPermission, "id" | "name" | "description">[];
}) {
  const client = useGqlClient();
  const createFolder = useCreateDataRoomFolderMutation(client);

  const createButtonRef = useRef<HTMLButtonElement>(null);
  const [folderName, setFolderName] = useState("");
  const [nameError, setNameError] = useState("");

  const { invalidateDealActivities, invalidateDataRoomFolder } =
    useInvalidateQueryKeys();

  const [permission, setPermission] = useState<Option>({
    label: "Loading...",
    description: "...",
    value: "",
  });

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.metaKey || event.ctrlKey) {
      if (event.key === "Enter") {
        event.preventDefault();
        if (createButtonRef.current) {
          createButtonRef.current.click();
        }
      }
    }
  };

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const [folders, setFolders] = useState<
    DataRoomFolderBrowseFragmentFragment["parentFolders"]
  >([]);

  useEffect(() => {
    if (props.isLoading || props.error || !props.data) {
      return;
    }

    setPermission({
      value: props.data.dataRoomFolder.permissions[0].id,
      label: props.data.dataRoomFolder.permissions[0].name,
      description: props.data.dataRoomFolder.permissions[0].description,
    });
    setFolders(props.data.dataRoomFolder.parentFolders);
  }, [props.data, props.isLoading, props.error]);

  const crumbs = [
    "Home",
    ...folders
      .slice(0, folders.length - 1)
      .map((f) => f.name)
      .reverse(),
  ];

  if (props.error) {
    return (
      <div>
        <p>Something went wrong</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center">
        <div>
          <H3>New folder</H3>
          <p className="text-sm text-gray-500 leading-none">
            In {`${crumbs.join(" / ")}`}
          </p>
        </div>
        <CloseIcon onClose={props.onClose} />
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!props.data) {
            return;
          }

          if (!folderName) {
            setNameError("Name is required");
            return;
          }

          createFolder.mutate(
            {
              dataRoomFolderID: props.data.dataRoomFolder.id,
              name: folderName,
              permissionIDs: permission.value ? [permission.value] : [],
            },
            {
              onSuccess: () => {
                if (props.data?.dataRoomFolder.id) {
                  invalidateDataRoomFolder(props.data.dataRoomFolder.id);
                }

                invalidateDealActivities();

                setFolderName("");

                props.onClose();
              },
            },
          );
        }}
      >
        <div className="mt-4">
          <TextInput
            name="folder name"
            id="folder_name"
            error={nameError}
            placeholder="The name of your new folder..."
            label="Name"
            value={folderName}
            onChange={(e) => {
              setNameError("");
              setFolderName(e.currentTarget.value);
            }}
          />

          {props.data &&
            props.data.dataRoomFolder.context === DataRoomContext.Deal ? (
            <div className="mt-3">
              <Dropdown
                label="Permission level"
                defaultOption={{
                  label: props.permissions[0].name,
                  description: props.permissions[0].description,
                  value: props.permissions[0].id,
                }}
                selectedOption={permission ?? undefined}
                onSelect={(o) => {
                  setPermission(o);
                }}
                options={props.permissions.map((p) => {
                  return {
                    value: p.id,
                    label: p.name,
                    description: p.description,
                  };
                })}
              />
            </div>
          ) : null}

          <div className="mt-12 flex justify-end">
            <Button
              ref={createButtonRef}
              type="submit"
              text="Create folder"
              variant="positive"
              isLoading={createFolder.isPending}
              isDisabled={folderName === ""}
              loadingText="Creating folder..."
            />
          </div>
        </div>
      </form>
    </div>
  );
}

function formatSelectedCount(files: number, folders: number) {
  if (files === 0 && folders === 0) {
    return "";
  }

  if (files > 0 && folders === 0) {
    return `${files} ${pluralize(files, "document", "documents")} selected`;
  }

  if (files === 0 && folders > 0) {
    return `${folders} ${pluralize(folders, "folder", "folders")} selected`;
  }

  return `${files} ${pluralize(files, "document", "documents")}, ${folders} ${pluralize(folders, "folder", "folders")} selected`;
}

function pluralize(count: number, singular: string, plural: string) {
  return count === 1 ? `${singular}` : `${plural}`;
}

function getSaveFileName(dataRoomFile: DataRoomFileQuery["dataRoomFile"]) {
  // Map file types to their standard extensions to improve mime type detection after download
  const fileTypeExtensions: Partial<Record<FileType, string>> = {
    [FileType.Pdf]: ".pdf",
    [FileType.Doc]: ".doc",
    [FileType.Docx]: ".docx",
    [FileType.Xls]: ".xls",
    [FileType.Xlsx]: ".xlsx",
    [FileType.Ppt]: ".ppt",
    [FileType.Pptx]: ".pptx",
    [FileType.Jpeg]: ".jpg",
    [FileType.Png]: ".png",
    [FileType.Tiff]: ".tiff",
    [FileType.Mp3]: ".mp3",
    [FileType.Mp4]: ".mp4",
    [FileType.Eml]: ".eml",
    [FileType.Msg]: ".msg",
    [FileType.Rtf]: ".rtf",
    [FileType.Zip]: ".zip",
    [FileType.RawText]: ".txt",
  };

  const expectedExtension = fileTypeExtensions[dataRoomFile.fileType];

  if (!expectedExtension) {
    return dataRoomFile.name;
  }

  const hasExtension = /\.[a-zA-Z]+$/.test(dataRoomFile.name);
  if (!hasExtension) {
    return `${dataRoomFile.name}${expectedExtension}`;
  }

  return dataRoomFile.name;
}
